import clsx from 'clsx'
import {KTSVG, toAbsoluteUrl} from '../../../../../_gori/helpers'
import {useIntl} from 'react-intl'
import {MapView} from './MapView'
import {forwardRef, useEffect, useImperativeHandle, useMemo, useState} from 'react'
import {Table, TableBody, TableWrapper} from '../../../../../_gori/partials/widgets'
import {PointItem, SuggestA1Batch} from '../../definitions'
import {buildPoints} from '../../utils'
import {useJsApiLoader} from '@react-google-maps/api'
import {compact} from 'lodash'

type SuggestSmartA1BatchProps = {
  data: SuggestA1Batch
}

export type SuggestSmartA1BatchRef = {
  getPoints: () => PointItem[]
}

const ERROR_KEY = 'invalid_address'

export const SuggestSmartA1Batch = forwardRef<SuggestSmartA1BatchRef, SuggestSmartA1BatchProps>(
  ({data}, ref) => {
    const {isLoaded} = useJsApiLoader({
      googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
      libraries: ['places'],
    })
    const intl = useIntl()
    const [selected, setSelected] = useState<string | null>(null)
    const [points, setPoints] = useState<PointItem[]>([])

    const onSelected = (item: string) => {
      setSelected(item)
    }

    useImperativeHandle(ref, () => ({
      getPoints: () => points,
    }))

    useEffect(() => {
      if (!isLoaded) return
      if (!data?.warehouses?.length) return

      let mounted = true

      buildPoints(data.warehouses)
        .then((res) => {
          if (mounted) setPoints(res)
        })
        .catch((err) => {
          console.error(err)
        })

      return () => {
        mounted = false
      }
    }, [isLoaded, data?.warehouses])

    const isErrorSelected = useMemo(() => selected === ERROR_KEY, [selected])

    const columns = useMemo(
      () => [
        {
          id: 'bae_id',
          Header: intl.formatMessage({id: 'BAE_ID'}),
          Cell: ({row}: {row: any}) => row.original.code || 'N/A',
          headerClassName: 'w-150px',
          className: 'w-150px',
          cellClassName: 'fw-bolder',
        },
        {
          id: 'shipTo',
          Header: intl.formatMessage({id: 'SHIP_TO'}),
          Cell: ({row}: {row: any}) => {
            const {street1, city, state, zip} = row.original
            return (
              <div className={clsx('fw-bolder', {'text-danger': isErrorSelected})}>
                {compact([street1, city, state, zip]).join(', ')}
              </div>
            )
          },
        },
      ],
      [intl, isErrorSelected]
    )

    const dataTable = useMemo(() => {
      if (!selected) return []
      if (isErrorSelected) {
        return data?.error_orders?.orders || []
      }
      const warehouse = data?.warehouses?.find((item) => item.id === selected)
      return warehouse?.orders || []
    }, [data?.error_orders?.orders, data?.warehouses, isErrorSelected, selected])

    return (
      <div>
        <div className={'border border-primary rounded p-6 my-6'}>
          <div className='mb-6'>
            <h3 className='fw-bolder fs-2 d-flex align-items-center mb-3'>
              {intl.formatMessage({id: 'SMART_A1_BATCH'})}
              <KTSVG
                path='/media/gori/a1/verify.svg'
                className={clsx('svg-icon svg-icon-1 ms-2 text-primary')}
              />
            </h3>
            <span className={clsx('text-gori fw-bolder fs-6')}>
              {intl.formatMessage({id: 'GIVE_A_SUGGESTION_USING_GORI_AI_OPTIMIZATION'})}
            </span>
          </div>
          <div className='d-flex gap-5 justify-content-between pe-10'>
            <MapView
              points={points}
              selected={points.find((item) => item.id === selected)?.title}
              zoomable={false}
            />
            <div className='d-flex flex-column justify-content-center'>
              <h4 className='text-primary mb-5'>
                {intl.formatMessage({id: 'SELECTED_ORDERS'})}: {data?.total}
              </h4>
              {data?.warehouses?.map((item) => {
                return (
                  <div
                    key={item.id}
                    onClick={() => onSelected(item.id)}
                    className={clsx('mb-3 cursor-pointer fw-bold fs-6 text-decoration-underline', {
                      'text-primary': selected === item.id,
                      'text-gray-700': selected !== item.id,
                    })}
                  >
                    {item.air_port} ({item.state} {item.city} {item.country}): {item.total}
                  </div>
                )
              })}
              {!!data?.error_orders?.total && (
                <div
                  onClick={() => onSelected(ERROR_KEY)}
                  className='cursor-pointer fw-bold fs-6 text-danger'
                >
                  N/A ({intl.formatMessage({id: 'INVALID_ADDRESS'})}): {data.error_orders.total}
                </div>
              )}
            </div>
          </div>
          {!!selected && (
            <TableWrapper>
              <TableBody height={'26vh'} minHeight={'26vh'} className='p-0 fw-bold'>
                <Table columns={columns} data={dataTable} />
              </TableBody>
            </TableWrapper>
          )}
        </div>
        {!!data?.error_orders?.total && (
          <div className='px-3 py-2 d-flex gap-2 rounded-2 error-section'>
            <img alt='err' src={toAbsoluteUrl('/media/gori/invalid.svg')} width={16} height={20} />
            <span>
              {intl.formatMessage(
                {id: 'INVALID_ORDERS_SMART_A1_BATCH_WARNING'},
                {
                  strong: (chunks) => <strong>{chunks}</strong>,
                }
              )}
            </span>
          </div>
        )}
      </div>
    )
  }
)
